import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  IconButton,
  Modal,
  Typography,
  TextField,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { postContractComment } from '../../../../requests/api/students';
import handleApiResponse from '../../../../utils/handleApiResponse';
import { getDateOnlyFormat } from '../../../../utils/helpers';
import { Comment, CommentsModalProps } from './types';
import useStyles from './styles';

function CommentsModal({
  open,
  onClose,
  contractId,
  comments,
  onCommentAdded,
}: CommentsModalProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [newComment, setNewComment] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [localComments, setLocalComments] = useState<Comment[]>(comments);
  const commentsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sortedComments = [...comments].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    });
    setLocalComments(sortedComments);
  }, [comments]);

  const scrollToBottom = () => {
    if (commentsListRef.current) {
      commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (open) {
      // Pequeño delay para asegurar que el DOM esté renderizado
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    // Scroll al final cuando cambian los comentarios
    scrollToBottom();
  }, [localComments]);

  const handleSendComment = async () => {
    if (!newComment.trim()) {
      enqueueSnackbar('Por favor ingresa un comentario', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await postContractComment(String(contractId), newComment.trim());

      enqueueSnackbar('Comentario agregado exitosamente', { variant: 'success' });
      setNewComment('');

      onCommentAdded();
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modalContainer}
    >
      <Card className={classes.modal}>
        <Box className={classes.modalContent}>
          <Box className={classes.modalTitleContainer}>
            <Typography className={classes.modalTitle}>
              Comentarios
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon color="primary" />
            </IconButton>
          </Box>

          <Box ref={commentsListRef} className={classes.commentsList}>
            {localComments.length > 0 ? (
              localComments.map((comment, index) => (
                <React.Fragment key={comment.id}>
                  <Box className={classes.commentItem}>
                    <Box className={classes.commentHeader}>
                      <Typography className={classes.commentAuthor}>
                        {comment.user?.name} {comment.user?.lastname}
                      </Typography>
                      <Typography className={classes.commentDate}>
                        {getDateOnlyFormat(comment.createdAt, '/', true)}
                      </Typography>
                    </Box>
                    <Typography className={classes.commentValue}>
                      {comment.comment}
                    </Typography>
                  </Box>
                  {index < localComments.length - 1 && <Divider />}
                </React.Fragment>
              ))
            ) : (
              <Typography className={classes.commentValue}>
                No hay comentarios aún
              </Typography>
            )}
          </Box>

          <Box className={classes.addCommentContainer}>
            <Typography className={classes.addCommentLabel}>
              Agregar comentario
            </Typography>
            <TextField
              placeholder="Escribir comentario..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendComment();
                }
              }}
              className={classes.addCommentInput}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSendComment}
                      disabled={loading || !newComment.trim()}
                      className={classes.sendIconButton}
                      edge="end"
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Card>
    </Modal>
  );
}

export default CommentsModal;
